import React, { useEffect, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';

import { ErrorReporter } from '../../utils/errorReporting';

interface PromiseRejectionHandlerProps {
  children: React.ReactNode;
}

const MQTT_ERROR_NAME_PATTERN = /^Mqtt[A-Za-z]*Error$/;

export function PromiseRejectionHandler({
  children,
}: PromiseRejectionHandlerProps): React.ReactNode {
  const [hasError, setError] = useState(false);

  useEffect(() => {
    const promiseRejectionHandler = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof Error && MQTT_ERROR_NAME_PATTERN.test(event.reason.name)) {
        // don't tear down the whole Portal over a MQTT failure.
        ErrorReporter.captureException(event.reason);
        event.preventDefault();
        return;
      }
      setError(true);
    };

    window.addEventListener('unhandledrejection', promiseRejectionHandler);
    return () => {
      window.removeEventListener('unhandledrejection', promiseRejectionHandler);
    };
  }, []);

  useEffect(() => {
    if (hasError) {
      setError(false);
      throw new Error('ErrorWrapper: Unhandled Rejection Error!');
    }
  }, [hasError]);

  return children;
}

interface ErrorWrapperProps {
  fallbackElement: React.ReactNode;
  children: React.ReactNode;
}

export function ErrorWrapper(props: Readonly<ErrorWrapperProps>): React.ReactElement {
  const location = useLocation();
  function ErrorFallback() {
    return props.fallbackElement;
  }

  return (
    <ErrorBoundary fallbackRender={ErrorFallback} resetKeys={[location.pathname]}>
      <PromiseRejectionHandler>{props.children}</PromiseRejectionHandler>
    </ErrorBoundary>
  );
}

// Re-throws so the error propagates past this boundary to the nearest ancestor one — the app-level
// ErrorWrapper. react-error-boundary does not catch an error thrown by its own fallback.
function DelegateToAncestorBoundary({ error }: FallbackProps): React.ReactElement {
  throw error;
}

/**
 * ErrorBoundary for use inside modules.
 *
 * Each module bundles its own copy of react-error-boundary, so `useErrorBoundary()` can only see a
 * boundary defined within the module's own tree — never the app-level one. This gives the module
 * that boundary, while delegating actual handling to the app-level ErrorWrapper by re-throwing.
 */
export function ModuleErrorBoundary({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const location = useLocation();
  return (
    <ErrorBoundary fallbackRender={DelegateToAncestorBoundary} resetKeys={[location.pathname]}>
      {children}
    </ErrorBoundary>
  );
}

export function throwWrapper(error: Error, handleError: (error: unknown) => void) {
  handleError(error);
  throw error;
}
